import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import ProductDetailTabTypeGrid from './ProductDetailTabTypeGrid';
import ProductDetailTabTypeModal from './ProductDetailTabTypeModal';
import { buildProductDetailTabTypeSavePayload } from './utils/mapProductDetailTabType';
import { validateProductDetailTabType } from './productDetailTabTypeValidation';

function ProductDetailTabTypeMain(props) {
  const [tabTypeList, setTabTypeList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedTabType, setSelectedTabType] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Product Detail Tab Types';

  const fetchTabTypes = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product-detail-tab-type/getAll`);
      if (response.status === 200) {
        setTabTypeList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load product detail tab types!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchTabTypes();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Product Detail Tab Types module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    setSelectedTabType(null);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add product detail tab type modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedTabType(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') return;

    const payload = buildProductDetailTabTypeSavePayload(data, getStorageValues('userId'));
    if (!validateProductDetailTabType(payload, setAlert)) return;

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product-detail-tab-type/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        CreateAlert(
          result.message || (isAdd ? 'Tab type added successfully!!' : 'Tab type updated successfully!!'),
          'Success',
          setAlert
        );
        await fetchTabTypes();
        if (isAdd) {
          closeModal();
        }
      } else {
        CreateAlert(result?.message || 'Failed to save tab type!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save tab type!!', 'Error', setAlert);
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Tab Key', selector: 'TabKey' },
      { name: 'Default Title', selector: 'DefaultTitle' },
      { name: 'Content Type', selector: 'ContentType' },
      { name: 'Placement', selector: 'Placement' },
      { name: 'Display Order', selector: 'DisplayOrder' },
    ];
    exportToExcel('Product Detail Tab Types', columns, tabTypeList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Tab Key', selector: 'TabKey' },
      { name: 'Default Title', selector: 'DefaultTitle' },
      { name: 'Content Type', selector: 'ContentType' },
      { name: 'Placement', selector: 'Placement' },
      { name: 'Display Order', selector: 'DisplayOrder' },
    ];
    exportToPDF('Product Detail Tab Types', columns, tabTypeList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchTabTypes}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <ProductDetailTabTypeGrid
        TabTypeData={tabTypeList}
        fetchTabTypes={fetchTabTypes}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <ProductDetailTabTypeModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedTabType}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductDetailTabTypeMain;
